import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Skeleton } from "@mui/material";
import classes from "../ChartStyles.module.css";
import ChartIndicator from "../../ChartIndicator/ChartIndicator";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const SOFT_PALETTE = {
  blue: "#4F7CFF",
  teal: "#24C8A5",
  orange: "#F5A524",
  cyan: "#2AA7E0",
  text: "#0F172A",
  muted: "#475569",
  grid: "rgba(148,163,184,0.15)",
};

const fontFamily = '"Gilroy Regular", "Gilroy", Inter, "Segoe UI", sans-serif';

const toRgba = (color, alpha = 1) => {
  if (!color || typeof color !== "string") return `rgba(79, 124, 255, ${alpha})`;
  const hex = color.replace("#", "");
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((value) => Number.isNaN(value))) return color;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeMetricValue = (value) => {
  if (typeof value === "number") return value;
  const numeric = Number(String(value ?? "").replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Math.abs(Number(value) || 0));

const breakdownScrollStyle = {
  marginTop: 8,
  paddingTop: 8,
  paddingRight: 12,
  borderTop: "1px solid rgba(226,232,240,0.92)",
  display: "grid",
  gap: 8,
  maxHeight: 220,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarGutter: "stable",
};

const breakdownRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(88px, max-content)",
  gap: 14,
  alignItems: "start",
  paddingRight: 4,
};

const breakdownLabelStyle = {
  fontSize: 12.5,
  color: SOFT_PALETTE.text,
  fontFamily: fontFamily,
  lineHeight: 1.4,
  overflowWrap: "anywhere",
  minWidth: 0,
};

const breakdownValueStyle = {
  fontSize: 12.5,
  color: SOFT_PALETTE.text,
  fontFamily: fontFamily,
  fontWeight: 600,
  whiteSpace: "nowrap",
  textAlign: "right",
  justifySelf: "end",
  paddingRight: 2,
};

const defaultBreakdownFormatter = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Math.abs(Number(value) || 0)
  );

const wrapLabel = (label = "", maxLength = 24) => {
  const text = String(label ?? "").trim();
  if (!text) return [""];
  if (text.length <= maxLength) return [text];
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 2);
};

const createHorizontalGradient = (chart, baseColor) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) return baseColor;
  const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
  gradient.addColorStop(0, toRgba(baseColor, 0.52));
  gradient.addColorStop(0.4, toRgba(baseColor, 0.82));
  gradient.addColorStop(1, toRgba(baseColor, 0.98));
  return gradient;
};

const getViewportSafePosition = (
  x,
  y,
  width,
  height,
  {
    offsetX = 18,
    offsetY = -12,
    padding = 12,
  } = {}
) => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth || 0 : 0;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight || 0 : 0;

  const preferredLeft = x + offsetX;
  const preferredTop = y + offsetY;

  return {
    left: Math.max(
      padding,
      Math.min(preferredLeft, Math.max(padding, viewportWidth - width - padding))
    ),
    top: Math.max(
      padding,
      Math.min(preferredTop, Math.max(padding, viewportHeight - height - padding))
    ),
  };
};

const HorizontalBarChart = ({
  chartData,
  loadingState,
  chartDataSet,
  filterOnChange,
  customCardClass,
  approveData,
  onCardClick,
  inline,
  valueSuffix = "",
  axisValueFormatter,
  tooltipValueFormatter,
  chartHeight: chartHeightProp,
  showHeading = true,
}) => {
  const [tooltip, setTooltip] = useState(null);
  const [pinnedTooltip, setPinnedTooltip] = useState(null);
  const [pinnedTooltipPosition, setPinnedTooltipPosition] = useState({
    left: 12,
    top: 12,
  });
  const pinnedTooltipRef = useRef(null);

  const labels = chartDataSet?.chartYAxisLabels || [];
  const fullLabels = chartDataSet?.fullLabels || labels;
  const values = chartData?.[0]?.data || [];
  const baseColor = Array.isArray(chartData?.[0]?.backgroundColor)
    ? chartData[0].backgroundColor[0]
    : chartData?.[0]?.backgroundColor || SOFT_PALETTE.blue;

  const dataSet = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: chartData?.[0]?.label || chartDataSet?.cardHeading || "",
          data: values.map((value) => normalizeMetricValue(value)),
          backgroundColor: (context) => createHorizontalGradient(context.chart, baseColor),
          borderColor: baseColor,
          borderWidth: 0,
          borderRadius: { topLeft: 0, topRight: 10, bottomLeft: 0, bottomRight: 10 },
          borderSkipped: "start",
          barPercentage: 0.72,
          categoryPercentage: 0.82,
          maxBarThickness: 24,
          minBarLength: 4,
        },
      ],
    }),
    [baseColor, chartData, chartDataSet?.cardHeading, labels, values]
  );

  const chartHeight = chartHeightProp || Math.max(360, labels.length * 42 + 80);
  const tooltipRows = chartDataSet?.tooltipRows || [];
  const breakdownValueFormatter =
    typeof chartDataSet?.breakdownValueFormatter === "function"
      ? chartDataSet.breakdownValueFormatter
      : defaultBreakdownFormatter;
  useEffect(() => {
    if (!pinnedTooltip?.visible) return undefined;

    const updatePosition = () => {
      const node = pinnedTooltipRef.current;
      if (!node) return;
      const { offsetWidth = 340, offsetHeight = 220 } = node;
      setPinnedTooltipPosition(
        getViewportSafePosition(
          pinnedTooltip.x,
          pinnedTooltip.y,
          offsetWidth,
          offsetHeight
        )
      );
    };

    const frameId = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePosition);
    };
  }, [pinnedTooltip]);

  useEffect(() => {
    if (!pinnedTooltip?.visible) return undefined;

    const handleOutsideClick = (event) => {
      if (pinnedTooltipRef.current?.contains(event.target)) return;
      setPinnedTooltip(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [pinnedTooltip]);

  const tooltipStyle = tooltip?.visible
    ? { opacity: 1, transform: "translateY(-6px)" }
    : { opacity: 0, transform: "translateY(0px)" };

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      animation: {
        duration: 400,
        easing: "easeInOutCubic",
      },
      onClick: (event, elements, chart) => {
        const point = elements?.[0];
        if (!point) {
          setPinnedTooltip(null);
          return;
        }

        const tooltipRow = tooltipRows?.[point.index];
        if (!Array.isArray(tooltipRow?.__othersItems) || tooltipRow.__othersItems.length === 0) {
          setPinnedTooltip(null);
          return;
        }

        const rect = chart?.canvas?.getBoundingClientRect?.();
        const nativeEvent = event?.native || event;
        const x = nativeEvent?.clientX ?? ((rect?.left || 0) + (event?.x || 0));
        const y = nativeEvent?.clientY ?? ((rect?.top || 0) + (event?.y || 0));
        const parsedValue = Math.abs(
          point?.element?.$context?.raw ??
            point?.element?.$context?.parsed?.x ??
            values?.[point.index] ??
            0
        );
        const labelValue =
          typeof tooltipValueFormatter === "function"
            ? tooltipValueFormatter(parsedValue)
            : formatNumber(parsedValue);

        setTooltip((prev) => (prev ? { ...prev, visible: false } : prev));
        setPinnedTooltip({
          visible: true,
          x,
          y,
          title: fullLabels?.[point.index] || labels?.[point.index] || "",
          value: `${labelValue}${valueSuffix ? ` ${valueSuffix}` : ""}`,
          breakdown: tooltipRow.__othersItems,
        });
      },
      layout: {
        padding: {
          left: 4,
          right: 12,
          top: 4,
          bottom: 4,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: (context) => {
            const { chart, tooltip: model } = context;
            if (!chart) return;
            if (pinnedTooltip?.visible) {
              setTooltip((prev) => (prev ? { ...prev, visible: false } : prev));
              return;
            }
            if (!model || model.opacity === 0) {
              setTooltip((prev) => (prev ? { ...prev, visible: false } : prev));
              return;
            }
            const point = model.dataPoints?.[0];
            if (!point) return;
            const rect = chart.canvas.getBoundingClientRect();
            const parsedValue = Math.abs(point.parsed?.x ?? point.raw ?? 0);
            const fullLabel = fullLabels?.[point.dataIndex] || labels?.[point.dataIndex] || "";
            const labelValue = typeof tooltipValueFormatter === "function"
              ? tooltipValueFormatter(parsedValue)
              : formatNumber(parsedValue);
            const labelText =
              approveData && point.dataset?.label?.includes("Response Time In Minutes")
                ? `${formatNumber(parsedValue)} min`
                : approveData && point.dataset?.label?.includes("Response Time In Hours")
                ? `${formatNumber(parsedValue)} hr`
                : `${labelValue}${valueSuffix ? ` ${valueSuffix}` : ""}`;
            const tooltipRow = tooltipRows?.[point.dataIndex] || null;

            setTooltip({
              visible: true,
              x: rect.left + model.caretX,
              y: rect.top + model.caretY,
              title: fullLabel,
              value: labelText,
              breakdown: Array.isArray(tooltipRow?.__othersItems)
                ? tooltipRow.__othersItems
                : [],
            });
          },
          backgroundColor: "#FFFFFF",
          titleColor: "#0F172A",
          bodyColor: "#0F172A",
          borderColor: "rgba(226,232,240,0.9)",
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          displayColors: false,
          titleFont: { size: 14, family: fontFamily, weight: "400" },
          bodyFont: { size: 14, family: fontFamily, weight: "400" },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            color: SOFT_PALETTE.grid,
            drawBorder: false,
          },
          border: { display: false },
          ticks: {
            color: SOFT_PALETTE.muted,
            padding: 10,
            font: { size: 14, family: fontFamily, weight: "400" },
            callback: (value) => {
              if (value === 0) return "0";
              const formatted = typeof axisValueFormatter === "function" ? axisValueFormatter(value) : value;
              return `${formatted}${valueSuffix ? ` ${valueSuffix}` : ""}`;
            },
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            color: SOFT_PALETTE.text,
            padding: 12,
            font: { size: 14, family: fontFamily, weight: "400" },
            callback: (value, index) => wrapLabel(labels?.[index] ?? value, 26),
          },
        },
      },
    }),
    [
      approveData,
      axisValueFormatter,
      fullLabels,
      labels,
      pinnedTooltip,
      tooltipRows,
      tooltipValueFormatter,
      values,
      valueSuffix,
    ]
  );

  const heading = (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 18,
          fontFamily: fontFamily,
          color: SOFT_PALETTE.text,
          marginBottom: 6,
          fontWeight: 600,
          letterSpacing: "-0.02em",
        }}
      >
        {chartDataSet?.cardHeading}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
        {chartDataSet?.chartIndicators?.map((e) => (
          <div key={e.value} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: e.color || baseColor,
                display: "inline-block",
              }}
            />
            <span
              style={{
                fontSize: 13,
                color: SOFT_PALETTE.muted,
                fontFamily: fontFamily,
                fontWeight: 400,
              }}
            >
              {e.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const indicatorOnlyHeading = (
    <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
      {chartDataSet?.chartIndicators?.map((e) => (
        <div key={e.value} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: e.color || baseColor,
              display: "inline-block",
            }}
          />
          <span
            style={{
              fontSize: 13,
              color: SOFT_PALETTE.muted,
              fontFamily: fontFamily,
              fontWeight: 400,
            }}
          >
            {e.value}
          </span>
        </div>
      ))}
    </div>
  );

  const chartInner = (
    <div style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%", position: "relative" }} onClick={onCardClick}>
      <div style={{ width: "100%", height: chartHeight, marginTop: 4 }}>
          <Bar data={dataSet} options={options} />
      </div>
      {tooltip?.visible ? (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y - 12,
            pointerEvents: "none",
            zIndex: 60,
            minWidth: 180,
            maxWidth: 320,
            background: "#FFFFFF",
            border: "1px solid rgba(226,232,240,0.9)",
            borderRadius: 12,
            boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
            padding: "10px 12px",
            color: SOFT_PALETTE.text,
            fontFamily: fontFamily,
            fontSize: 13,
            lineHeight: 1.5,
            opacity: tooltipStyle.opacity,
            transform: tooltipStyle.transform,
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          <div style={{ color: SOFT_PALETTE.muted, marginBottom: 4, fontSize: 12.5 }}>{tooltip.title}</div>
          <div style={{ fontWeight: 400, color: SOFT_PALETTE.text, fontSize: 13 }}>{tooltip.value}</div>
          {Array.isArray(tooltip.breakdown) && tooltip.breakdown.length > 0 ? (
            <div style={{ ...breakdownScrollStyle, gap: 6, maxHeight: 180 }}>
              <div
                style={{
                  fontSize: 12,
                  color: SOFT_PALETTE.muted,
                  fontFamily: fontFamily,
                  letterSpacing: "0.01em",
                }}
              >
                Remaining breakdown
              </div>
              {tooltip.breakdown.map((entry, index) => (
                <div key={`${entry?.label || "item"}-${index}`} style={breakdownRowStyle}>
                  <div style={breakdownLabelStyle}>
                    {entry?.label || "-"}
                  </div>
                  <div style={breakdownValueStyle}>
                    {breakdownValueFormatter(entry?.value ?? 0)}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {pinnedTooltip?.visible ? (
        <div
          ref={pinnedTooltipRef}
          style={{
            position: "fixed",
            left: pinnedTooltipPosition.left,
            top: pinnedTooltipPosition.top,
            zIndex: 70,
            minWidth: 220,
            maxWidth: 360,
            background: "#FFFFFF",
            border: "1px solid rgba(226,232,240,0.95)",
            borderRadius: 16,
            boxShadow: "0px 18px 40px rgba(15,23,42,0.18)",
            padding: "12px 14px",
            color: SOFT_PALETTE.text,
            fontFamily: fontFamily,
            fontSize: 13,
            lineHeight: 1.45,
            pointerEvents: "auto",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 6,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: SOFT_PALETTE.muted,
                  fontSize: 12.5,
                  marginBottom: 4,
                  overflowWrap: "anywhere",
                }}
              >
                {pinnedTooltip.title}
              </div>
              <div style={{ fontWeight: 600, color: SOFT_PALETTE.text, fontSize: 13 }}>
                {pinnedTooltip.value}
              </div>
            </div>
            <button
              type="button"
              aria-label="Close tooltip"
              onClick={() => setPinnedTooltip(null)}
              style={{
                border: "none",
                background: "transparent",
                color: SOFT_PALETTE.muted,
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                padding: 0,
                flexShrink: 0,
              }}
            >
              {"\u00D7"}
            </button>
          </div>
          {Array.isArray(pinnedTooltip.breakdown) &&
          pinnedTooltip.breakdown.length > 0 ? (
            <div style={breakdownScrollStyle}>
              {pinnedTooltip.breakdown.map((entry, index) => (
                <div key={`${entry?.label || "item"}-${index}`} style={breakdownRowStyle}>
                  <div style={breakdownLabelStyle}>
                    {entry?.label || "-"}
                  </div>
                  <div style={breakdownValueStyle}>
                    {breakdownValueFormatter(entry?.value ?? 0)}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const skeleton = (
    <>
      <Skeleton width="180px" height="24px" style={{ marginBottom: 10 }} />
      <Skeleton
        width="100%"
        height={Math.max(240, chartHeight)}
        variant="rectangular"
        style={{ borderRadius: 16, backgroundColor: "rgba(226,232,240,0.6)" }}
      />
    </>
  );

  if (inline) {
    return (
      <div onClick={onCardClick} style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%" }}>
        {loadingState ? skeleton : <>{showHeading ? heading : indicatorOnlyHeading}{chartInner}</>}
      </div>
    );
  }

  return (
    <div
      className={customCardClass}
      onClick={onCardClick}
      style={{
        background: "#fff",
        borderRadius: "18px",
        border: "1px solid rgba(226,232,240,0.8)",
        boxShadow: "0px 8px 30px rgba(15,23,42,0.06)",
        padding: "20px",
        cursor: onCardClick ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {loadingState ? skeleton : <>{showHeading ? heading : indicatorOnlyHeading}{chartInner}</>}
    </div>
  );
};

export default memo(HorizontalBarChart);
